/**
 * MastheadCover — journal/periodical title page. A series bar (series ·
 * Confidential), a centred title whose byline IS the SITE NAME (COVER-CONTENT
 * BATCH), then the abstract excerpt. Natural for the two-column academic
 * family (quarterly / review / proceedings), where it is the per-theme
 * default cover.
 *
 * Theme-aware: the series rule, centred title, and abstract justification
 * read the active report theme tokens (headingFont / bodyFont / headingColor
 * / bodyTextColor / primaryColor). All copy reads EXISTING report data — the
 * abstract is the persisted report summary (`resolveAbstractFull`). The
 * confidential disclaimer renders as a bottom-right footnote at 80% of the
 * prior size. Null-safe: an empty summary collapses the abstract block rather
 * than rendering a stray "Abstract—" with no body. (The report-type-derived
 * "Index Terms" line was removed — it was boilerplate-per-type, not
 * report-content-derived, so no clean population existed.)
 */
import React from 'react';
import {
  resolveCoverTitle,
  resolveSiteWordmark,
  resolveBylineParts,
  resolveAbstractExcerpt,
  resolveAbstractFull,
} from '../../../../lib/reportRendering/journalCoverData';
import { DEFAULT_PRIMARY_COVER_LOGO_URL } from '../../../../lib/reportBranding';
import { roleColor, roleColorAlpha } from '../../../../lib/reportRoleColors';
import { reportTypographySizes, reportHeadingInk } from '../../../../lib/reportSectionHeading';
import { COVER_MIN_HEIGHT_PX } from '../../../../lib/reportCoverTokens';
import { formatPageNumber, normalizePageNumberFormat } from '../../../../lib/reportLayoutTokens';
import { REPORT_COVER_DISCLAIMER } from '../../../../lib/reportLegalDisclaimer';
import type { CoverProps } from './types';
import { CoverHeaderPlatformCredit, platformCreditToneForTheme } from '../ReportPlatformCredit';

/**
 * DEFECT — doubled "Confidential. Beta workflow…" disclaimer. The resolved
 * `disclaimer` prop defaults to {@link REPORT_COVER_DISCLAIMER} (the EXACT beta
 * caveat) when no archetype-specific override exists, and the footer ALSO
 * hard-codes that same caveat below it. Render the prop block ONLY when it is a
 * DISTINCT archetype override so the beta line is never duplicated.
 */
function isDistinctDisclaimer(disclaimer: string | undefined): disclaimer is string {
  if (typeof disclaimer !== 'string') return false;
  const trimmed = disclaimer.trim();
  return trimmed.length > 0 && trimmed !== REPORT_COVER_DISCLAIMER.trim();
}

export const MastheadCover: React.FC<CoverProps> = ({
  report, theme, branding,
  disclaimer,
  tocEntries,
  tocPageNumbers,
  tocPageNumberFormat,
  boundedStandfirst = false,
}) => {
  // DESIGN PARITY NOTE (#156): the masthead cover intentionally does NOT
  // render a logo — it is a journal/periodical title page whose identity is
  // carried by the series bar + centred title. `effectiveBranding` is resolved
  // for parity with the other covers but left UNRENDERED here by design;
  // whether to surface the primary logo in the series bar (CoverPrimaryLogo)
  // is a deferred design call left for review. `void` marks the omission
  // explicit (mirrors `void seriesBrand` below).
  const effectiveBranding = branding ?? {
    audienceMode: 'internal' as const,
    siteWordmark: resolveSiteWordmark(report, undefined),
    primaryLogoUrl: theme.logoUrl ?? DEFAULT_PRIMARY_COVER_LOGO_URL,
    secondaryLogoUrl: null,
    primaryLogoAlt: theme.companyName ?? 'Primary logo',
    secondaryLogoAlt: 'Customer logo',
  };
  void effectiveBranding;

  const title = resolveCoverTitle(report);
  const wordmark = resolveSiteWordmark(report, branding);
  // COVER-CONTENT BATCH (1/2): the masthead header byline is the SITE NAME,
  // not "{ORG} ENGINEERING · PREPARED FOR …". The org identity is still
  // carried by the series bar above; the title page now leads with the site.
  const author = wordmark.toUpperCase();
  const affiliation = resolveBylineParts(report, branding).join(' · ');
  const abstract = boundedStandfirst
    ? resolveAbstractExcerpt(report)
    : resolveAbstractFull(report);

  // Abstract / affiliation / index-term copy size derives from the resolved
  // body size (headingScale + themeTypographyScale aware) × the theme's
  // bodyTextScale, instead of the old fixed px values — so the masthead body
  // copy tracks the active preset's density like the rest of the report.
  const bodyFontSize = `calc(${reportTypographySizes(theme).body} * ${theme.bodyTextScale ?? 1})`;

  // DEFECT 8 — compact 2-COLUMN numbered index threaded from the caller
  // (deriveTocEntries + exec-summary de-dup already applied). Mirrors the
  // standalone TOC's <3-entry guard so a near-empty index never renders.
  const carriesToc = Array.isArray(tocEntries) && tocEntries.length >= 3;
  const tocFmt = normalizePageNumberFormat(tocPageNumberFormat);

  return (
    <div
      className="relative flex flex-col"
      data-testid="cover-masthead"
      data-cover-variant="masthead"
      style={{
        color: roleColor(theme, 'body'),
        // DEFECT 10 — WHITE BLEED: the masthead root previously stopped at
        // COVER_MIN_HEIGHT_PX (520px), leaving white below it inside the
        // (cream-tinted) A4 cover box. Stretch the root to FILL its
        // `[data-section="cover"]` box — the SAME printable-A4 calc the cover
        // box uses (index.css cover min-height, token-locked) — so the cream
        // page background paints edge-to-edge and no white shows below. The
        // 520px floor is kept via max() so a tiny abstract never collapses the
        // root below the historical minimum on the continuous viewer. The
        // flex-column + footer mt-auto pins the disclaimer to the bottom edge.
        minHeight: `max(${COVER_MIN_HEIGHT_PX}px, calc(var(--report-page-height, 297mm) - 14mm - 16mm))`,
      }}
    >
      {/* Series bar. */}
      <div
        className="flex items-center justify-between text-[10px] uppercase tracking-[0.18em] pb-2.5"
        style={{ color: roleColor(theme, 'body'), fontFamily: theme.bodyFont, borderBottom: `1px solid ${roleColor(theme, 'rule')}`, opacity: 0.95 }}
        data-testid="cover-masthead-series"
      >
        <span />
        <span data-testid="cover-confidential-tag">Confidential</span>
      </div>
      {/* GATEWAY POWER platform credit — right-aligned under the series rule,
          in the title block's pt-11 gap (zero height; see
          CoverHeaderPlatformCredit). */}
      <CoverHeaderPlatformCredit
        tone={platformCreditToneForTheme(theme)}
        color={roleColor(theme, 'muted')}
        fontFamily={theme.bodyFont}
        testId="cover-masthead-platform-credit"
      />

      {/* Centred title + author + affiliation. */}
      <div
        className="text-center px-2 pt-11 pb-5"
        style={{ borderBottom: `1px solid ${roleColorAlpha(theme, 'ink', 0.33)}` }}
      >
        <h1
          id="cover-title"
          className="mx-auto"
          // Title size keys off the resolved headingScale (editorial >
          // technical > compact) instead of a fixed clamp; the responsive
          // ceiling is bounded by the resolved h2 size. Display font falls
          // back to headingFont.
          style={{
            fontFamily: theme.displayFont || theme.headingFont,
            color: reportHeadingInk(theme),
            fontSize: `clamp(26px, 4vw, calc(${reportTypographySizes(theme).h2} * 1.67))`,
            fontWeight: 500,
            maxWidth: '24ch',
            lineHeight: 1.12,
          }}
          data-testid="cover-masthead-title"
        >
          {title}
        </h1>
        <div
          className="text-[11px] uppercase tracking-[0.05em] mt-4"
          style={{ color: roleColor(theme, 'body'), fontFamily: theme.bodyFont, opacity: 0.8 }}
          data-testid="cover-masthead-author"
          data-cover-site-name-header="true"
        >
          <span data-testid="cover-site-name-header">{author}</span>
        </div>
        {affiliation && (
          <div
            className="italic mt-1"
            style={{ color: roleColor(theme, 'body'), fontFamily: theme.headingFont, opacity: 0.8, fontSize: bodyFontSize }}
            data-testid="cover-masthead-affiliation"
          >
            {affiliation}
          </div>
        )}
      </div>

      {/* Abstract excerpt. (COVER-CONTENT BATCH 5: the report-type-derived
          "Index Terms" line was removed — it was boilerplate-per-type, not
          report-content-derived, so no clean population existed.) */}
      <div className="px-8 pt-6 pb-8">
        {abstract && (
          <p
            className="leading-relaxed text-left"
            style={{ color: roleColor(theme, 'body'), fontFamily: theme.bodyFont, hyphens: 'auto', fontSize: bodyFontSize }}
            data-testid="cover-masthead-abstract"
          >
            <span
              className="text-[10px] font-bold uppercase tracking-[0.08em] mr-2 float-left"
              // Run-in label takes the brand accent ink (editorial run-in
              // convention) instead of the heading ink. roleColor is null-safe.
              style={{ color: roleColor(theme, 'accent'), fontFamily: theme.bodyFont }}
            >
              Abstract–
            </span>
            {abstract}
          </p>
        )}
      </div>

      {/* DEFECT 8 — COMPACT 2-COLUMN NUMBERED INDEX, on the cover below the
          abstract (replaces the standalone `[data-section="toc"]` page, which
          the caller suppresses via `coverCarriesToc`). Editorial language: a
          hairline top rule under a mono small-caps "Contents" eyebrow, then a
          balanced two-column numbered list — mono numeral + serif title +
          right-aligned page number with a hairline column rule between the two
          columns. Pure layout; the entries/page-numbers are caller-computed. */}
      {carriesToc && (
        <div className="px-8 pt-5 pb-2" data-testid="cover-masthead-toc">
          <div
            className="text-[10px] font-bold uppercase tracking-[0.18em] pb-2 mb-3"
            style={{
              color: roleColor(theme, 'muted'),
              fontFamily: theme.bodyFont,
              borderBottom: `1px solid ${roleColor(theme, 'rule')}`,
            }}
          >
            Contents
          </div>
          <ol
            className="m-0 p-0 list-none"
            style={{
              columnCount: 2,
              columnGap: '2rem',
              columnFill: 'balance',
              columnRule: `1px solid ${roleColorAlpha(theme, 'ink', 0.12)}`,
            }}
          >
            {tocEntries!.map((entry, i) => {
              const page = tocPageNumbers?.[entry.id];
              const hasPage = typeof page === 'number' && Number.isFinite(page);
              return (
                <li
                  key={entry.id}
                  className="flex items-baseline gap-2 py-[3px] [break-inside:avoid] [-webkit-column-break-inside:avoid]"
                  data-testid={`cover-toc-entry-${entry.id}`}
                >
                  <span
                    className="shrink-0 text-right tabular-nums"
                    style={{ width: '1.4rem', fontFamily: theme.bodyFont, fontSize: '10px', color: roleColor(theme, 'accent'), letterSpacing: '0.02em' }}
                  >
                    {i + 1}
                  </span>
                  <a
                    href={`#${entry.id}`}
                    className="flex-1 truncate"
                    style={{ fontFamily: theme.bodyFont, fontSize: '11.5px', color: roleColor(theme, 'body'), textDecoration: 'none' }}
                  >
                    {entry.label}
                  </a>
                  {hasPage && (
                    <span
                      className="shrink-0 tabular-nums"
                      style={{ fontFamily: theme.bodyFont, fontSize: '10px', color: roleColor(theme, 'muted') }}
                      data-testid={`cover-toc-page-${entry.id}`}
                    >
                      {formatPageNumber(page!, tocFmt)}
                    </span>
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      )}

      {/* DEFECT 9 — CONFIDENTIAL DISCLAIMER footer: a centered, muted, bottom-
          PINNED line (was a bottom-right footnote). `mt-auto` pushes it to the
          bottom edge of the now-full-A4 flex-column cover; a hairline top rule
          separates it from the index above. The resolved `disclaimer` prop is
          rendered above the fixed beta-workflow caveat. */}
      <div
        className="mt-auto px-8 pt-3 pb-2 text-center"
        style={{ borderTop: `1px solid ${roleColorAlpha(theme, 'ink', 0.12)}` }}
        data-testid="cover-masthead-disclaimer"
      >
        {isDistinctDisclaimer(disclaimer) && (
          <div
            className="text-[8px] leading-relaxed"
            style={{ color: roleColor(theme, 'body'), opacity: 0.7, fontFamily: theme.bodyFont }}
          >
            {disclaimer}
          </div>
        )}
        <div
          className="text-[8px] leading-relaxed mt-1 mx-auto max-w-xl"
          style={{ color: roleColor(theme, 'body'), opacity: 0.7, fontFamily: theme.bodyFont }}
          data-testid="cover-masthead-beta-disclaimer"
        >
          Confidential. Beta workflow: directional guidance only; may contain errors.
          Independently validate all findings with qualified advisors before any decision.
        </div>
      </div>
    </div>
  );
};
